import { TextField, TextFieldProps } from '@material-ui/core';
import React, { useEffect, useMemo, useState } from 'react';
import { AutocompleteInput } from '../AutocompleteInput';
import states from './states.json';

type stateType = {
  name: string;
  abbreviation: string;
};

export type StateSelectFieldProps = Partial<TextFieldProps> & {
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: (event: React.FocusEvent<HTMLDivElement>) => void;
  shouldDisplayAbbreviation?: boolean;
  shouldDisableClearable?: boolean;
  disabled?: boolean;
  renderInput?: (params: TextFieldProps) => React.ReactNode;
};

export function StateSelectField({
  value: valueProp = '',
  onBlur = () => undefined,
  onChange = () => undefined,
  id = '',
  shouldDisplayAbbreviation = true,
  shouldDisableClearable = false,
  disabled,
  ...props
}: StateSelectFieldProps): JSX.Element {
  const [value, setValue] = useState<stateType | null>(null);
  const statesByAbbr: { [key: string]: stateType } = useMemo(
    () => states.reduce((prev, current) => ({ ...prev, [current.abbreviation]: current }), {}),
    [],
  );
  const sortedStateAbbreviations = states.map((each) => each.abbreviation).sort((a, b) => a.localeCompare(b));

  useEffect(() => {
    if (value !== statesByAbbr[valueProp]) {
      setValue(statesByAbbr[valueProp] || null);
    }
  }, [valueProp]);

  return (
    <AutocompleteInput
      disableClearable={shouldDisableClearable || undefined}
      clearOnEscape={true}
      onBlur={onBlur}
      options={sortedStateAbbreviations}
      getOptionLabel={(option) => {
        if (shouldDisplayAbbreviation) {
          return option;
        }

        return states.find((each) => each.abbreviation === option)?.name ?? '';
      }}
      value={value?.abbreviation ?? ''}
      onChange={(_, changedAbbreviation) => {
        onChange(changedAbbreviation);

        const changedState = states.find((each) => each.abbreviation === changedAbbreviation);

        if (changedState) {
          setValue(changedState);
        }
      }}
      renderInput={
        props.renderInput ||
        ((param) => (
          <TextField
            label="State"
            {...param}
            {...props}
            inputProps={{ ...param.inputProps, 'aria-label': 'state-field-input-select' }}
          />
        ))
      }
      id={id}
      data-testid={id}
      disabled={disabled}
      getOptionSelected={(option, value) => option === value}
    />
  );
}
export default StateSelectField;
